import { useState, useCallback } from 'react';
import { gastosFijosAPI } from '../services/gastosFijosAPI';
import { validarMontoGasto, organizarPorGrupo, calcularSubtotales } from '../utils/validacionGastos';
import { GRUPOS_ORDEN } from '../utils/constantesGastos';

const ESTADO_INICIAL_GASTOS = {};
const ESTADO_INICIAL_SUBTOTALES = {};
for (const grupo of GRUPOS_ORDEN) {
  ESTADO_INICIAL_GASTOS[grupo] = [];
  ESTADO_INICIAL_SUBTOTALES[grupo] = 0;
}

export const useGastosFijos = ({ onToast } = {}) => {
  const [gastos, setGastos] = useState(ESTADO_INICIAL_GASTOS);
  const [subtotales, setSubtotales] = useState(ESTADO_INICIAL_SUBTOTALES);
  const [totalGeneral, setTotalGeneral] = useState(0);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  const buscar = useCallback(async (mes, anio) => {
    setLoading(true);
    setError(null);

    try {
      const data = await gastosFijosAPI.getGastosFijos(mes, anio);

      const gastosOrganizados = organizarPorGrupo(data.items || []);
      setGastos(gastosOrganizados);
      setSubtotales(data.subtotales || ESTADO_INICIAL_SUBTOTALES);
      setTotalGeneral(data.totalGeneral || 0);
    } catch (err) {
      console.error('Error al cargar gastos fijos:', err);
      const mensaje = 'Error al cargar los gastos fijos';
      setError(mensaje);
      if (onToast) {
        onToast({ type: 'error', message: mensaje });
      }
    } finally {
      setLoading(false);
    }
  }, [onToast]);

  const guardarValor = useCallback(async (idCategoria, valor, mes, anio) => {
    // Validar antes de enviar
    const validacion = validarMontoGasto(valor);
    if (!validacion.valid) {
      return validacion;
    }

    setSaving(true);
    setError(null);

    try {
      await gastosFijosAPI.guardarGastoFijo({
        idCategoria,
        mes,
        anio,
        valor: Number(String(valor).trim()),
      });

      // Actualizar el item local sin reload
      setGastos(prevGastos => {
        const nuevosGastos = { ...prevGastos };
        for (const grupo of GRUPOS_ORDEN) {
          nuevosGastos[grupo] = prevGastos[grupo].map(item =>
            item.idCategoria === idCategoria
              ? { ...item, valor: Number(String(valor).trim()) }
              : item
          );
        }

        // Recalcular subtotales con los items actualizados
        const todosLosItems = GRUPOS_ORDEN.flatMap(g => nuevosGastos[g]);
        const { subtotales: nuevosSubtotales, totalGeneral: nuevoTotal } =
          calcularSubtotales(todosLosItems);

        setSubtotales(nuevosSubtotales);
        setTotalGeneral(nuevoTotal);

        return nuevosGastos;
      });

      if (onToast) {
        onToast({ type: 'success', message: 'Valor guardado correctamente' });
      }

      return { valid: true };
    } catch (err) {
      console.error('Error al guardar gasto fijo:', err);
      const mensaje = 'Error al guardar el valor';
      setError(mensaje);
      if (onToast) {
        onToast({ type: 'error', message: mensaje });
      }
      return { valid: false, error: 'save_failed' };
    } finally {
      setSaving(false);
    }
  }, [onToast]);

  return {
    gastos,
    subtotales,
    totalGeneral,
    loading,
    saving,
    error,
    buscar,
    guardarValor,
  };
};
